import {Component, EventEmitter, Input, OnInit, Output} from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn,
  Validators
} from "@angular/forms";
import {MatDialog} from "@angular/material/dialog";
import {ConfirmDialogComponent} from "../confirm-dialog/confirm-dialog.component";
import {CommonModule} from '@angular/common';
import {DynamicFieldDirective} from '../dynamic-field/dynamic-field.directive';
import {SubmitEvent} from "../../model/events";
import {FieldResource, FieldValidationResource} from "../../core/modules/openapi";
import {BehaviorSubject} from "rxjs";

@Component({
  exportAs: "dynamicForm",
  selector: "dynamic-form",
  imports: [ReactiveFormsModule, CommonModule, DynamicFieldDirective],
  template: `
    <form class="dynamic-form" [formGroup]=form (submit)="onSubmit($event)">
      <ng-container *ngFor="let field of fields;" dynamicField [field]="field" [group]=form
                    (visibilityGroupChanged)="onVisibilityGroupChanged($event)"
                    [currentVisibilityGroup]="currentVisibilityGroup"
                    [subject]="behaviorSubject.asObservable()">
      </ng-container>
    </form>
  `,
  standalone: true,
  styleUrls: ['./dynamic-form.component.sass']
})
export class DynamicFormComponent implements OnInit {
  @Input() fields: FieldResource[] = [];
  @Input() form: FormGroup;
  @Output() submit: EventEmitter<any> = new EventEmitter<any>();
  currentVisibilityGroup: string;
  behaviorSubject = new BehaviorSubject<string>('');

  //form: FormGroup;

  get value() {
    return this.form.value;
  }

  constructor(private fb: FormBuilder, public dialog: MatDialog) {
  }

  ngOnInit() {
    this.form = this.createControls();
  }

  onReset(event: Event) {
    this.form.reset();

  }

  get valid(): boolean {
    return this.form.valid;
  }

  onSubmit(event: Event) {
    let btn = this.disableSubmit(event);
    event.preventDefault();
    event.stopPropagation();
    if (this.form.valid && !btn?.hint) {
      this.submit.emit(new SubmitEvent({"source": String(btn?.value), "data": this.value}));
      this.enableSubmit(undefined);
    } else if (this.form.valid && btn?.hint) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        maxWidth: "400px",
        data: {
          "title": "Confirmar",
          "message": btn.hint
        }
      });

      dialogRef.afterClosed().subscribe(dialogResult => {
        let confirmationResult = dialogResult;

        if (confirmationResult) {
          this.submit.emit(new SubmitEvent({"source": String(btn?.value), "data": this.value}));
        }
        this.enableSubmit(undefined);
      });
    } else {
      this.validateAllFormFields(this.form);
      this.enableSubmit(undefined);
    }
  }

  createControls() {
    let boolVal: any;
    const group = this.fb.group({});
    this.fields.forEach(field => {
      if (!('enabled' in field)) {
        field.enabled = true;
      }

      if (field.conditionallyEnabled && !field.checked) {
        field.enabled = false;
      } else if (field.conditionallyEnabled && field.checked) {
        field.enabled = true;
      } else {
        field.conditionallyEnabled = false;
      }

      if (field.type === "button") return;

      if (field.type === "checkbox") {
        boolVal = false;
        if (String(field.value) === "True") {
          boolVal = true;
        }
        field.value = boolVal;
      }


      if (field.type === "select" && field.multiple) {
        field.value = field.value as string;
      }

      if (field.type === "date") {
        field.value = field.value + "T00:00:00";
      }

      if (field.type === "radio" || field.type === "visibility_group_selector") {
        field.value = field.value as string;
      }

      if (field.type === "visibility_group_selector") {
        const visibilityGroup = field.possibleValues?.find(pv => pv.value === field.value)?.visibilityGroupSelector;
        if (visibilityGroup) {
          this.onVisibilityGroupChanged(visibilityGroup);
        }
      }

      const control = new FormControl({
        value: field.value,
        disabled: !field.enabled
      }, this.bindValidations(field.validations || []));
      group.addControl(field.name, control);
    });
    return group;
  }

  //add validations
  bindValidations(validations: FieldValidationResource[]) {
    if (validations.length > 0) {
      const validList: (ValidatorFn | null | undefined)[] = [];
      validations.forEach(valid => {
        let validator;
        if (valid.validator === "Validators.required") {
          validator = Validators.required;
        } else if (valid.validator === "Validators.requiredTrue") {
          validator = Validators.requiredTrue;
        } else if (valid.validator === "Validators.minLength") {
          validator = Validators.minLength(Number(valid.validatorArg));
        } else if (valid.validator === "Validators.maxLength") {
          validator = Validators.maxLength(Number(valid.validatorArg));
        } else if (valid.validator === "Validators.min") {
          validator = Validators.min(Number(valid.validatorArg));
        } else if (valid.validator === "Validators.max") {
          validator = Validators.max(Number(valid.validatorArg));
        } else if (valid.validator === "Validators.pattern" && valid.validatorArg) {
          validator = Validators.pattern(valid.validatorArg);
        } else if (valid.validator === "Validators.email") {
          validator = Validators.email;
        }
        validList.push(validator);
      });
      return Validators.compose(validList);
    }
    return null;
  }

  validateAllFormFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.controls[field];
      control?.markAsTouched({onlySelf: true});
    });
  }

  validateFormFields() {
    let formGroup = this.form;
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.controls[field];
      control?.markAsTouched({onlySelf: true});
    });
  }

  disableSubmit(event: any): FieldResource | undefined {
    return this.changeSubmitState(event, false);
  }

  enableSubmit(event: any): FieldResource | undefined {
    return this.changeSubmitState(event, true);
  }

  changeSubmitState(event: any, state: boolean): FieldResource | undefined {
    let btn;
    this.fields.forEach(field => {
      if (field['type'] === "button") {
        field['enabled'] = state;
        if (event && field.displayName === event['submitter']['innerText']) {
          btn = field;
        }
        if (!event) {
          btn = field;
        }
      }
    });
    return btn;
  }

  onButtonClicked(event: any) {
    console.log(event); // handle button clicked here.
  }

  onVisibilityGroupChanged($event: string) {
    this.currentVisibilityGroup = $event;
    this.behaviorSubject.next($event);
  }
}
